import { __ } from "@wordpress/i18n"
import { 
  useBlockProps, 
  InspectorControls,
  RichText,
  useInnerBlocksProps,
  MediaUpload,
  MediaUploadCheck
} from "@wordpress/block-editor"
import { 
  PanelBody,
  Notice,
  Button,
  TextControl,
  ToggleControl
} from "@wordpress/components"

export default function Edit({ attributes, setAttributes }) {
  const blockProps = useBlockProps({
    className: 'community-commitment-section'
  })

  // Default content
  const defaultTitle = attributes.mainTitle || 'PuroClean Cares®: <br/>Commitment to Community'
  const defaultLinkText = attributes.linkText || 'Learn More About Our Community Commitment'
  const defaultLinkUrl = attributes.linkUrl || '/community'
  const reverseColumns = attributes.reverseColumns || false
  const hideLink = attributes.hideLink || false

  // Configure InnerBlocks for description paragraphs
  const innerBlocksProps = useInnerBlocksProps(
    { className: 'text-lg mb-8 space-y-4' },
    {
      allowedBlocks: ['core/paragraph'],
      template: [
        ['core/paragraph', { 
          content: 'At PuroClean, we are dedicated not only to restoring your property but also to rebuilding our communities. Through our PuroClean Cares® initiatives, we extend our service beyond property recovery by actively engaging in community support and charitable activities. Our commitment to social responsibility drives us to partner with local and national organizations, contributing through volunteering, donations, and community service.',
          placeholder: 'Enter first paragraph...'
        }],
        ['core/paragraph', { 
          content: 'Every PuroClean office is a vital part of its community, and through PuroClean Cares®, we strive to make a positive impact in the lives of those we serve.',
          placeholder: 'Enter second paragraph...'
        }]
      ],
      templateLock: false
    }
  )

  return (
    <>
      <InspectorControls>
        <PanelBody title={__('Community Commitment Settings', 'puroclean')} initialOpen={true}>
          <Notice status="info" isDismissible={false}>
            <p>
              <strong>Content:</strong> Click on any text to edit directly<br />
              <strong>Description:</strong> Each paragraph is a separate block - add, remove, or reorder freely<br />
              <strong>Images:</strong> Use the controls below to select custom images<br />
              <strong>Preview:</strong> Shows exactly how it appears on frontend
            </p>
          </Notice>
          
          {/* Main Image Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              {__('Main Image:', 'puroclean')}
            </label>
            <MediaUploadCheck>
              <MediaUpload
                onSelect={(media) => setAttributes({ 
                  mainImage: {
                    url: media.url,
                    id: media.id,
                    alt: media.alt || 'Community Teaching and Support'
                  }
                })}
                allowedTypes={['image']}
                value={attributes.mainImage?.id || 0}
                render={({ open }) => (
                  <div>
                    {attributes.mainImage?.url ? (
                      <div>
                        <img 
                          src={attributes.mainImage.url} 
                          alt={attributes.mainImage.alt}
                          style={{ maxWidth: '200px', marginBottom: '10px', display: 'block' }}
                        />
                        <Button onClick={open} variant="secondary" style={{ marginRight: '10px' }}>
                          {__('Change Image', 'puroclean')}
                        </Button>
                        <Button 
                          onClick={() => setAttributes({ 
                            mainImage: { url: '', id: 0, alt: 'Community Teaching and Support' }
                          })} 
                          variant="link" 
                          isDestructive
                        >
                          {__('Remove Image', 'puroclean')}
                        </Button>
                      </div>
                    ) : (
                      <Button onClick={open} variant="secondary">
                        {__('Select Main Image', 'puroclean')}
                      </Button>
                    )}
                  </div>
                )}
              />
            </MediaUploadCheck>
          </div>
          
          {/* Link Settings */}
          <div style={{ marginBottom: '20px' }}>
            <ToggleControl
              label={__('Hide Link', 'puroclean')}
              checked={hideLink}
              onChange={(value) => setAttributes({ hideLink: value })}
              help={__('Hide the entire link section', 'puroclean')}
            />
          </div>
          
          {!hideLink && (
            <>
              <div style={{ marginBottom: '20px' }}>
                <TextControl
                  label={__('Link Text:', 'puroclean')}
                  value={defaultLinkText}
                  onChange={(value) => setAttributes({ linkText: value })}
                  help={__('The complete link text (e.g., "Learn More About Our Community Commitment")', 'puroclean')}
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <TextControl
                  label={__('Link URL:', 'puroclean')}
                  value={defaultLinkUrl}
                  onChange={(value) => setAttributes({ linkUrl: value })}
                  help={__('The URL the link points to', 'puroclean')}
                />
              </div>
            </>
          )}
          
          {/* Layout Settings */}
          <div style={{ marginBottom: '20px' }}>
            <ToggleControl
              label={__('Reverse Columns', 'puroclean')}
              checked={reverseColumns}
              onChange={(value) => setAttributes({ reverseColumns: value })}
              help={__('Move the image to the left side instead of right', 'puroclean')}
            />
          </div>
          
          {/* PuroClean Cares Logo Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              {__('PuroClean Cares Logo:', 'puroclean')}
            </label>
            <MediaUploadCheck>
              <MediaUpload
                onSelect={(media) => setAttributes({ 
                  puroCaresLogo: {
                    url: media.url,
                    id: media.id,
                    alt: media.alt || 'PuroClean Cares'
                  }
                })}
                allowedTypes={['image']}
                value={attributes.puroCaresLogo?.id || 0}
                render={({ open }) => (
                  <div>
                    {attributes.puroCaresLogo?.url ? (
                      <div>
                        <img 
                          src={attributes.puroCaresLogo.url} 
                          alt={attributes.puroCaresLogo.alt}
                          style={{ maxWidth: '200px', marginBottom: '10px', display: 'block' }}
                        />
                        <Button onClick={open} variant="secondary" style={{ marginRight: '10px' }}>
                          {__('Change Logo', 'puroclean')}
                        </Button>
                        <Button 
                          onClick={() => setAttributes({ 
                            puroCaresLogo: { url: '', id: 0, alt: 'PuroClean Cares' }
                          })} 
                          variant="link" 
                          isDestructive
                        >
                          {__('Remove Logo', 'puroclean')}
                        </Button>
                      </div>
                    ) : (
                      <Button onClick={open} variant="secondary">
                        {__('Select PuroClean Cares Logo', 'puroclean')}
                      </Button>
                    )}
                  </div>
                )}
              />
            </MediaUploadCheck>
          </div>
        </PanelBody>
      </InspectorControls>

      <div {...blockProps}>
        <div className="py-4 md:py-16 bg-white">
          <div className="max-w-content">
            <div className={`grid grid-cols-1 lg:grid-cols-12 items-center gap-4 md:gap-16 ${reverseColumns ? 'lg:grid-flow-col-dense' : ''}`}>
              {/* Content Column - Always 8/12 width */}
              <div className={`flex items-center lg:col-span-8 ${reverseColumns ? 'order-2 lg:order-2 lg:col-start-5' : 'order-2 lg:order-1 lg:col-start-1'}`}>
                <div className="w-full">
                  <div className="flex flex-col md:flex-row md:items-center items-start justify-between mb-4">
                    <RichText
                      tagName="h1"
                      placeholder={__('Enter main title...', 'puroclean')}
                      value={defaultTitle}
                      onChange={(value) => setAttributes({ mainTitle: value })}
                      allowedFormats={['core/bold', 'core/italic']}
                      className="~text-2xl/4xl font-bold mb-4 md:mb-0"
                    />
                    {attributes.puroCaresLogo?.url ? (
                      <img 
                        src={attributes.puroCaresLogo.url} 
                        alt={attributes.puroCaresLogo.alt} 
                        width="182" 
                        height="91" 
                        className="flex-shrink-0 md:ml-4 w-[155px] md:w-[182px]"
                        loading="lazy"
                      />
                    ) : (
                      <img 
                        src="/wp-content/themes/puroclean-tw/assets/img/puro-cares.png" 
                        alt="PuroClean Cares" 
                        width="182" 
                        height="91" 
                        className="flex-shrink-0 md:ml-4 w-[155px] md:w-[182px]"
                        loading="lazy"
                      />
                    )}
                  </div>
                  
                  {/* Description - Individual Paragraph Blocks */}
                  <div {...innerBlocksProps} />
                  
                  {!hideLink && (
                    <span className="text-lg flex flex-col md:flex-row items-start md:items-center mt-4 gap-0 md:gap-2 font-semibold">
                      <a 
                        href={defaultLinkUrl} 
                        className="inline-flex items-center text-primary underline hover:scale-105 transition-all duration-500 group"
                        style={{ color: '#dc2626' }}
                      >
                        <RichText
                          tagName="span"
                          placeholder={__('Enter complete link text...', 'puroclean')}
                          value={defaultLinkText}
                          onChange={(value) => setAttributes({ linkText: value })}
                          allowedFormats={['core/bold', 'core/italic']}
                          className="leading-tight"
                        />
                        <svg 
                          className="w-5 h-5 ml-2 group-hover:rotate-45 transition-transform duration-500 flex-shrink-0" 
                          fill="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path d="M12 4L10.59 5.41L16.17 11H4V13H16.17L10.59 18.59L12 20L20 12L12 4Z" />
                        </svg>
                      </a>
                    </span>
                  )}
                </div>
              </div>
              
              {/* Image Column - Always 4/12 width */}
              <div className={`lg:col-span-4 ${reverseColumns ? 'order-1 lg:order-1 lg:col-start-1' : 'order-1 lg:order-2 lg:col-start-9'}`}>
                <figure className="size-large rounded-lg">
                  {attributes.mainImage?.url ? (
                    <img 
                      src={attributes.mainImage.url} 
                      alt={attributes.mainImage.alt}
                      className="rounded-lg w-full h-auto"
                      loading="lazy"
                    />
                  ) : (
                    <img 
                      src="/wp-content/themes/puroclean-tw/assets/img/people-teaching.png" 
                      alt="Community Teaching and Support"
                      className="rounded-lg w-full h-auto"
                      loading="lazy"
                    />
                  )}
                </figure>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
} 